import { formatPhoneNumber, PhoneNumberE164, TelecomPhoneLineId } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { SecondaryButton } from "components/ui/secondary-button";
import { useGetSmsConversationByPhoneLineQuery } from "generated/graphql";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePhoneLines } from "store/selectors/telecom";
import { SendSmsPanel } from "./send-sms-panel";
import { SmsConversationHistory } from "./sms-conversation-history";

interface SmsTranscriptProps {
    phoneLineId: TelecomPhoneLineId;
    phoneNumber: PhoneNumberE164;
    senderName: string;
    onClose?: () => void;
}

export const SmsTranscript: React.FC<SmsTranscriptProps> = props => {
    const { phoneLineId, phoneNumber, senderName } = props;
    const scrollRef = useRef<HTMLDivElement>(null);
    const phoneLines = usePhoneLines();
    const hasOwnPhoneLine = phoneLines?.length > 0;
    const [showOwnConversation, setShowOwnConversation] = useState(false);

    const handleStartConversation = useCallback(() => {
        setShowOwnConversation(true);
    }, []);

    const handleDismissOwnConversation = useCallback(() => {
        setShowOwnConversation(false);
    }, []);

    const getSmsConversation = useGetSmsConversationByPhoneLineQuery(
        {
            request: {
                phoneLineId,
                phoneNumber,
            },
        },
        {
            refetchInterval: 30_000,
        },
    );

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [getSmsConversation.data]);

    return (
        <div className="flex flex-col h-[calc(100vh-8rem)]">
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
                <SmsConversationHistory
                    messages={
                        getSmsConversation.data?.getSmsConversationByPhoneLine ?? []
                    }
                    showSenderName={true}
                />
            </div>
            <div className="flex-none border-t border-gray-100 pt-4 mt-4">
                <div className="flex items-center justify-between gap-4">
                    <InlineBanner type="info">
                        This is {senderName}&apos;s conversation with{" "}
                        {formatPhoneNumber(phoneNumber)}.
                    </InlineBanner>
                    <div>
                        {hasOwnPhoneLine ? (
                            <SecondaryButton
                                className="whitespace-nowrap py-3 px-6 text-lg"
                                onClick={handleStartConversation}>
                                Send From My Number
                            </SecondaryButton>
                        ) : null}
                    </div>
                </div>
            </div>
            <SendSmsPanel
                isOpen={showOwnConversation}
                onDismiss={handleDismissOwnConversation}
                recipient={phoneNumber}
            />
        </div>
    );
};
